/**
 * TripStatusPanel — driver-side: 4 quick-status buttons during active window.
 * Passenger-side: read-only timeline.
 *
 * Polls every 30s while the ride is in its active window so passengers see
 * the driver's updates without a manual refresh. Outside the window the
 * card hides itself (no clutter on past or far-future rides).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { listRideStatus, postRideStatusApi } from '../lib/api';
import { translateError, useT, jsLocale } from '../lib/i18n';
import { hapticError, hapticSuccess } from '../lib/telegram';
import { Card, CardHint, CardTitle } from './Card';
import { useState } from 'react';

const ACTIVE_BEFORE_MS = 2 * 60 * 60 * 1000;
const ACTIVE_AFTER_MS = 24 * 60 * 60 * 1000;

const STATUS_CODES = ['leaving_soon', 'at_pickup', 'departed', 'arrived'] as const;

interface Props {
  rideId: string;
  /** Driver-only: needed to gate the action buttons by active window. Passenger mode omits this — server enforces window. */
  departureAt?: string;
  /** True when the viewer is the driver of this ride (shows action buttons). */
  asDriver: boolean;
  /** True when the viewer has any participation in the ride (passenger or driver). */
  canRead: boolean;
}

export function TripStatusPanel({
  rideId,
  departureAt,
  asDriver,
  canRead,
}: Props): JSX.Element | null {
  const { t, locale } = useT();
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const inWindow = departureAt
    ? (() => {
        const dep = new Date(departureAt).getTime();
        const now = Date.now();
        return now >= dep - ACTIVE_BEFORE_MS && now <= dep + ACTIVE_AFTER_MS;
      })()
    : true;
  const visible = canRead;

  const q = useQuery({
    queryKey: ['ride-status', rideId],
    queryFn: () => listRideStatus(rideId),
    enabled: visible,
    refetchInterval: inWindow ? 30_000 : false,
  });

  const post = useMutation({
    mutationFn: (status: (typeof STATUS_CODES)[number]) => postRideStatusApi(rideId, status),
    onSuccess: async () => {
      hapticSuccess();
      await qc.invalidateQueries({ queryKey: ['ride-status', rideId] });
    },
    onError: (e: Error) => {
      hapticError();
      setError(translateError(t, e));
    },
  });

  if (!visible) return null;

  return (
    <Card className="mt-3 space-y-3">
      <CardTitle>{t('tripStatus.title')}</CardTitle>

      {asDriver && (
        <>
          {!inWindow && <CardHint>{t('tripStatus.windowClosed')}</CardHint>}
          {inWindow && (
            <>
              <CardHint>{t('tripStatus.driverHint')}</CardHint>
              <div className="grid grid-cols-2 gap-2">
                {STATUS_CODES.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => post.mutate(s)}
                    disabled={post.isPending}
                    className="rounded-input bg-brand-green/10 px-3 py-2.5 text-sm font-semibold text-brand-green disabled:opacity-50"
                  >
                    {t(`tripStatus.${s}` as 'tripStatus.leaving_soon')}
                  </button>
                ))}
              </div>
            </>
          )}
        </>
      )}

      {error && (
        <p className="rounded-input bg-destructive/10 p-2 text-xs text-destructive">{error}</p>
      )}

      {q.data && q.data.items.length === 0 && (
        <CardHint>{t('tripStatus.empty')}</CardHint>
      )}

      {q.data && q.data.items.length > 0 && (
        <ol className="space-y-2">
          {q.data.items.map((u) => {
            const ts = new Date(u.createdAt);
            const time = ts.toLocaleTimeString(jsLocale(locale), {
              hour: '2-digit',
              minute: '2-digit',
              hour12: false,
            });
            return (
              <li
                key={u.id}
                className="flex items-start gap-3 rounded-input bg-tg-bg-secondary p-3"
              >
                <span className="mt-0.5 inline-block h-2 w-2 flex-none rounded-full bg-brand-green" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-tg-text">
                    {t(`tripStatus.${u.status}` as 'tripStatus.leaving_soon')}
                  </p>
                  {u.note && <p className="text-xs text-tg-hint">"{u.note}"</p>}
                </div>
                <span className="flex-none text-xs text-tg-hint">{time}</span>
              </li>
            );
          })}
        </ol>
      )}
    </Card>
  );
}
